import { CTA_SOURCES, PAGE_SECTIONS } from "@punchup/analytics";
import { Comedian, getComedianByIDOrThrow } from "@/data/comedians";

import { Check } from "lucide-react";
import { FOLLOW_LOCATIONS } from "@/data/follows";
import { SignInSignUpForm } from "@punchup/ui-common/components/signin";
import { useEffect, useState } from "react";
import { useUser } from "@punchup/data/legacy/hooks";

interface Props {
  comedianToFollow: Pick<Comedian, "id" | "slug"> &
    Partial<Pick<Comedian, "display_name_short">>;
  className?: string;
  pageSection?: PAGE_SECTIONS;
}

const valuePropositions = (comedianName: string) => [
  "Be the first to know about shows in your area",
  "Watch new specials before they're released",
  `Get exclusive updates directly from ${comedianName}`,
  "No spam, just the good stuff!",
];

export default function SubscribeForShows({
  comedianToFollow,
  className,
  pageSection,
}: Props) {
  const user = useUser();
  const authenticated = user === undefined ? null : !!user;
  const [comedian, setComedian] = useState<Comedian | null>(null);

  useEffect(() => {
    getComedianByIDOrThrow(comedianToFollow.id).then(setComedian);
  }, [comedianToFollow]);

  if (authenticated === null || comedian === null) {
    return null;
  }

  if (!authenticated) {
    return (
      <div className={className}>
        <SubscribeForShowsCard comedian={comedian}>
          <SignInSignUpForm
            comedianToFollow={{
              id: comedianToFollow.id,
              slug: comedianToFollow.slug,
              display_name_short: comedianToFollow.display_name_short || "",
            }}
            followLocation={FOLLOW_LOCATIONS.COMEDIAN_LANDING_PAGE}
            ctaSource={CTA_SOURCES.COMEDIAN_PAGE_GUEST_LIST_SIGNUP}
            {...(pageSection ? { pageSection } : {})}
            buttonText="Subscribe for free"
            variant="minimal"
            toggleVariant="radio"
            buttonLayout="horizontal"
            redirectTo={`/${comedianToFollow.slug}`}
            routeNewUserToOnboarding
            policyFooterType="comedian"
          />
        </SubscribeForShowsCard>
      </div>
    );
  }

  return null;
}

function SubscribeForShowsCard({
  children,
  comedian,
}: {
  children: React.ReactNode;
  comedian: Comedian;
}) {
  return (
    <div className="relative flex flex-row justify-center w-full bg-neutral-100 border-[0.5px] rounded-lg shadow-[0_0_4px_rgba(72,72,72,0.25)]">
      <div className="flex flex-col items-start md:items-center justify-center w-full px-4 py-3 md:justify-between md:flex-row gap-y-2.5 md:gap-y-0 md:gap-x-4 md:px-6 lg:px-8 md:py-6">
        <div className="flex flex-col items-start justify-center md:gap-y-4 mt-px md:mt-0">
          <p className="md:hidden text-base font-semibold text-left md:text-lg text-primary-blue leading-tight">
            Be the first to know when {comedian.display_name_short} tours
          </p>
          <p className="hidden md:block text-base font-semibold text-left md:text-lg text-primary-blue leading-tight">
            Join {comedian.display_name_short}&apos;s Guest List
          </p>
          <div className="hidden md:flex flex-col gap-y-3 pb-1">
            {valuePropositions(comedian.display_name_short).map(
              (valueProposition, index) => (
                <div key={index} className="flex flex-row items-center gap-2">
                  <Check
                    width={18}
                    height={18}
                    strokeWidth={3}
                    className="hidden text-green-600 md:block"
                  />
                  <p className="text-sm text-left md:block text-secondary-blue md:text-primary-blue font-light">
                    {valueProposition}
                  </p>
                </div>
              )
            )}
          </div>
        </div>
        <div className="flex flex-row justify-end items-center">{children}</div>
      </div>
    </div>
  );
}
